/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link, Typography, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { SectionName, UpsellType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import UpsellCard from 'bundles/unified-description-page-common/components/modules/upsell/UpsellCard';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useMWebStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import courseraPlusLogo from 'images/unified-description-page/courseraplus-blue.png';
import standaloneUpsellModuleCourseraPlus from 'images/unified-description-page/individual-upsell-coursera-plus.png';
import mWebXDPCourseraPlusUsell from 'images/unified-description-page/mweb-xdp-mobile-coursera-plus-upsell.png';
import upsellModuleCourseraPlus from 'images/unified-description-page/upsell-module-coursera-plus.png';

import _t from 'i18n!nls/xdp';

type Props = {
  // This will be added dynamically from UpsellModule.
  amountOfCards?: number;
};

const styles = {
  cta: css`
    color: var(--cds-color-interactive-primary);
  `,
};

const CourseraPlusUpsellCard = ({ amountOfCards }: Props) => {
  const isMWebStreamlinedXDP = useMWebStreamlinedXDP();
  const isDesktop = useMediaQuery(breakpoints.up('lg'));
  const isStandaloneCard = isDesktop && amountOfCards === 1;

  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const trackingData = {
    upsellType: 'coursera_plus' as UpsellType,
    pageSection: {
      sectionName: 'upsell' as SectionName,
    },
    ...eventingProductData,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_upsell_card',
    trackingData
  );

  let backgroundImageSrc = upsellModuleCourseraPlus;
  if (isStandaloneCard) {
    backgroundImageSrc = standaloneUpsellModuleCourseraPlus;
  } else if (isMWebStreamlinedXDP) {
    backgroundImageSrc = mWebXDPCourseraPlusUsell;
  }

  return (
    <UpsellCard
      logo={courseraPlusLogo}
      title={
        <Typography variant="h1semibold" component="h2">
          {_t('Open new doors with Coursera Plus')}
        </Typography>
      }
      description={
        <Typography2 variant="bodySecondary" component="p">
          {_t(
            'Unlimited access to 7,000+ world-class courses, hands-on projects, and job-ready certificate programs - all included in your subscription'
          )}
        </Typography2>
      }
      cta={
        <Link
          component={TrackedA}
          href="/courseraplus"
          trackingName="upsell_cta_courseraplus"
          typographyVariant="subtitleMedium"
          variant="quiet"
          css={styles.cta}
          ariaLabel={_t('Learn more about Coursera Plus')}
          icon={<ArrowNextIcon />}
          onClick={() => {
            track('click_upsell_card', trackingData);
          }}
        >
          {/* eslint-disable-next-line @coursera/custom-rules/informative-links */}
          {_t('Learn more')}
        </Link>
      }
      backgroundImage={backgroundImageSrc}
      backgroundPosition="90%"
      backgroundColor={isMWebStreamlinedXDP ? '#eaf3ff' : undefined}
      amountOfCards={amountOfCards}
      ref={visibilityTrackingRef}
    />
  );
};

export default CourseraPlusUpsellCard;
